import { Tooltip } from 'antd';
import React, { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Channel, ChannelConfiguration } from 'api/notifications';
import { FinancialNotificationConfig } from 'api/settings-financials/types';
import { Checkbox } from 'components/atoms/checkbox';
import { RoutePaths } from 'constant';
import { useTranslate } from 'hooks/use-translation';
import { useFinancialNotificationsConfigMutation } from 'queries/financial-notifications';
import { useQuerySlackConfig } from 'queries/slack-integration';
import { Description, Helper, NotfiyTitle } from './styles';

const StyledCheckBox = styled(Checkbox)`
  display: flex;
  align-items: center;
`;
const CheckBoxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-04);
`;
const Wrapper = styled.div`
  margin-bottom: var(--spacing-09);
`;

const IntegrateSlack = () => {
  const translate = useTranslate();

  return (
    <>
      <div>{translate('settings.financials.enable.slack.tootip.title')}</div>
      <div>
        {translate('settings.financials.enable.slack.tootip.message')}
        <Link to={RoutePaths.slackIntegration}>{` ${translate(
          'settings.financials.enable.slack.redirect.text',
        )}.`}</Link>
      </div>
    </>
  );
};

export const FinancialAlertChannelsToggle: FC<{
  budgetConfig: FinancialNotificationConfig;
  projectHours: FinancialNotificationConfig;
  projectHoursConfigId: number;
  projectBudgetConfigId: number;
}> = ({
  budgetConfig,
  projectHours,
  projectHoursConfigId,
  projectBudgetConfigId,
}) => {
  const translate = useTranslate();

  const { data } = useQuerySlackConfig();
  const isSlackEnabled = !!(data && data.length > 0);

  const { mutateAsync: updateProjectHours } =
    useFinancialNotificationsConfigMutation(projectHoursConfigId);

  const { mutateAsync: updateProjectBudget } =
    useFinancialNotificationsConfigMutation(projectBudgetConfigId);

  const initialValues = Object.keys(budgetConfig.channels).filter((channel) => {
    return budgetConfig.channels[channel];
  });

  const [channels, setChannels] = useState(initialValues);
  const updateValues = async (values) => {
    const channelMap = Object.values(Channel).reduce((map, val) => {
      return {
        ...map,
        [val]: (values as string[]).includes(val),
      };
    }, {});

    await updateProjectHours({
      ...projectHours,
      channels: channelMap as ChannelConfiguration,
    });

    await updateProjectBudget({
      ...budgetConfig,
      channels: channelMap as ChannelConfiguration,
    });

    setChannels(values);
  };

  return (
    <Wrapper>
      <NotfiyTitle>
        {translate('settings.financials.project_budget_alerts.title')}
      </NotfiyTitle>
      <Description>
        {translate('settings.financials.project_budget_alerts.description')}
      </Description>
      <Helper>
        {translate('settings.financials.notifications.notify.via')}
      </Helper>
      <Checkbox.Group value={channels} onChange={updateValues}>
        <CheckBoxWrapper>
          {Object.values(Channel)
            .filter((val) => val !== Channel.Slack) // todo:remove this once slack is done
            .map((val) => {
              return val === Channel.Slack && !isSlackEnabled ? (
                <Tooltip title={<IntegrateSlack />}>
                  <StyledCheckBox disabled value={val}>
                    {translate(
                      `settings.financials.notifications.notify.via.${val}`,
                    )}
                  </StyledCheckBox>
                </Tooltip>
              ) : (
                <StyledCheckBox value={val}>
                  {translate(
                    `settings.financials.notifications.notify.via.${val}`,
                  )}
                </StyledCheckBox>
              );
            })}
        </CheckBoxWrapper>
      </Checkbox.Group>
    </Wrapper>
  );
};
